import { exportBackupUrl, useReports } from '../api/queries';
import { fmtILS } from '../utils/format';

export function ReportsView() {
  const { data, isLoading } = useReports();

  if (isLoading || !data) {
    return <div className="p-6 text-text2">Loading reports…</div>;
  }

  return (
    <div className="p-3 md:p-6 overflow-y-auto overscroll-contain h-full">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <h2 className="text-lg font-bold">Reports</h2>
        <a className="btn btn-sm md:btn btn-secondary" href={exportBackupUrl()}>
          Download backup (JSON)
        </a>
      </div>

      {/* ---- Headline cards ---- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 md:gap-4 mb-4 md:mb-6">
        <Stat label="Active leads" value={data.totals.activeLeads} />
        <Stat label="Won this period" value={data.totals.wonLeads} accent="green" />
        <Stat label="Active pipeline (yearly)" value={fmtILS(data.totals.activeYearly)} />
        <Stat
          label="Weighted forecast (yearly)"
          value={fmtILS(data.totals.forecastYearly)}
          accent="accent"
          hint="Sum of dealValueYearly × stage probability across active leads"
        />
        <Stat label="Active NRE" value={fmtILS(data.totals.activeNre)} />
        <Stat label="Won NRE" value={fmtILS(data.totals.wonNre)} accent="green" />
        <Stat label="Won yearly" value={fmtILS(data.totals.wonYearly)} accent="green" />
        <Stat label="Forecast NRE" value={fmtILS(data.totals.forecastNre)} accent="accent" />
      </div>

      {/* ---- Goal progress ---- */}
      {data.goals && (data.goals.goalDeals || data.goals.goalRevenue) && (
        <GoalCard goals={data.goals} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card title="Conversion funnel">
          {data.conversion.map((c) => {
            const total = c.enteredCount || 1;
            const passedPct = (c.passedThroughCount / total) * 100;
            return (
              <div key={c.stageId} className="mb-3">
                <div className="flex justify-between text-xs">
                  <span>{c.stageName}</span>
                  <span className="text-text3">
                    {c.passedThroughCount}/{c.enteredCount} ({Math.round(passedPct)}%)
                  </span>
                </div>
                <div className="h-2 bg-surface2 rounded mt-1 overflow-hidden">
                  <div className="h-full bg-accent rounded" style={{ width: `${passedPct}%` }} />
                </div>
              </div>
            );
          })}
        </Card>

        <Card title="Velocity">
          <div className="text-sm space-y-3">
            <div>
              <div className="text-text3 text-xs">Avg days from creation to close-won</div>
              <div className="text-2xl font-bold">
                {data.velocity.avgDaysToWin == null
                  ? '—'
                  : `${Math.round(data.velocity.avgDaysToWin)} days`}
              </div>
            </div>
            <div>
              <div className="text-text3 text-xs">Avg days to close-lost</div>
              <div className="text-2xl font-bold">
                {data.velocity.avgDaysToLost == null
                  ? '—'
                  : `${Math.round(data.velocity.avgDaysToLost)} days`}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card title="Why we lose deals" subtitle="Lost-reason breakdown">
          {data.lostReasons.length === 0 ? (
            <div className="text-text3 text-sm">No closed-lost deals yet.</div>
          ) : (
            <ReasonBars rows={data.lostReasons} barClass="bg-red/70" />
          )}
        </Card>
        <Card title="Why we win" subtitle="Won-reason breakdown">
          {data.wonReasons.length === 0 ? (
            <div className="text-text3 text-sm">No closed-won deals yet.</div>
          ) : (
            <ReasonBars rows={data.wonReasons} barClass="bg-green/70" />
          )}
        </Card>
      </div>

      <Card title="Conversion by source" subtitle="Where do our wins come from?">
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="text-left text-xs text-text3 border-b border-border">
                <th className="py-2">Source</th>
                <th>Total</th>
                <th>Won</th>
                <th>Lost</th>
                <th>Win rate</th>
              </tr>
            </thead>
            <tbody>
              {data.bySource.map((s) => (
                <tr key={s.source} className="border-b border-border/50">
                  <td className="py-2">{s.source}</td>
                  <td>{s.total}</td>
                  <td className="text-green">{s.won}</td>
                  <td className="text-red">{s.lost}</td>
                  <td>{Math.round(s.winRate * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-4">
        <Card title="Pipeline by Owner">
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-sm min-w-[420px]">
              <thead>
                <tr className="text-left text-xs text-text3 border-b border-border">
                  <th className="py-2">Owner</th>
                  <th>Active</th>
                  <th>Won</th>
                  <th>Pipeline (yearly)</th>
                </tr>
              </thead>
              <tbody>
                {data.byOwner.map((o) => (
                  <tr key={o.userId ?? 'unassigned'} className="border-b border-border/50">
                    <td className="py-2">{o.name}</td>
                    <td>{o.activeCount}</td>
                    <td>{o.wonCount}</td>
                    <td className="text-green">{fmtILS(o.pipelineYearly)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: number | string;
  accent?: 'green' | 'red' | 'accent';
  hint?: string;
}) {
  const accentClass =
    accent === 'green'
      ? 'text-green'
      : accent === 'red'
        ? 'text-red'
        : accent === 'accent'
          ? 'text-accent2'
          : '';
  return (
    <div className="bg-surface border border-border rounded-xl p-3 md:p-4 min-w-0" title={hint}>
      <div className="text-xs text-text3">{label}</div>
      <div className={`text-xl md:text-2xl font-bold mt-1 truncate ${accentClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-text3 mt-1">{hint}</div>}
    </div>
  );
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-surface border border-border rounded-xl p-4 md:p-5">
      <h3 className="text-sm font-semibold text-text2">{title}</h3>
      {subtitle && <div className="text-xs text-text3 mb-3">{subtitle}</div>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ReasonBars({
  rows,
  barClass,
}: {
  rows: { reason: string; count: number; yearly: number }[];
  barClass: string;
}) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.reason}>
          <div className="flex justify-between text-xs">
            <span>{r.reason}</span>
            <span className="text-text3">
              {r.count} · {fmtILS(r.yearly)}/yr
            </span>
          </div>
          <div className="h-2 bg-surface2 rounded mt-1 overflow-hidden">
            <div
              className={`h-full rounded ${barClass}`}
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function GoalCard({
  goals,
}: {
  goals: NonNullable<ReturnType<typeof useReports>['data']>['goals'];
}) {
  if (!goals) return null;
  const dealsPct = goals.goalDeals
    ? Math.min(100, (goals.actualDeals / goals.goalDeals) * 100)
    : null;
  const revPct = goals.goalRevenue
    ? Math.min(100, (goals.actualRevenue / goals.goalRevenue) * 100)
    : null;
  const fmtQ = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
  return (
    <div className="bg-surface border border-border rounded-xl p-4 md:p-5 mb-4 md:mb-6">
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
        <h3 className="text-sm font-semibold text-text2">Quarterly goal</h3>
        <span className="text-xs text-text3">
          {fmtQ(goals.quarterStart)} → {fmtQ(goals.quarterEnd)}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.goalDeals != null && (
          <Bar
            label="Deals won"
            actual={goals.actualDeals}
            goal={goals.goalDeals}
            pct={dealsPct ?? 0}
          />
        )}
        {goals.goalRevenue != null && (
          <Bar
            label="Revenue (yearly)"
            actual={fmtILS(goals.actualRevenue)}
            goal={fmtILS(goals.goalRevenue)}
            pct={revPct ?? 0}
          />
        )}
      </div>
    </div>
  );
}

function Bar({
  label,
  actual,
  goal,
  pct,
}: {
  label: string;
  actual: string | number;
  goal: string | number;
  pct: number;
}) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span>{label}</span>
        <span className="text-text3">
          {actual} / {goal} ({Math.round(pct)}%)
        </span>
      </div>
      <div className="h-3 bg-surface2 rounded overflow-hidden">
        <div
          className={pct >= 100 ? 'h-full bg-green' : 'h-full bg-accent'}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
